class NSSTabsGroup extends HTMLElement {
  constructor() {
    super();

    this.debug = false;

    this.els = {};

    this.enableAutoswitch = "false";
    this.autoswitchDelay = 3000;
    this.autoswitchPause = false;
  }

  connectedCallback() {
    if (this.debug) console.log(`[NSS][Tabs Group] Connected.`);

    this.init();

    this.initAutoplay();

    this.addEventListener(`click`, this.handleClick.bind(this));
    this.addEventListener(`mouseenter`, this.handleMouseEnter.bind(this));
    this.addEventListener(`mouseleave`, this.handleMouseLeave.bind(this));

    const editorEvents = [
      `shopify:inspector:activate`,
      `shopify:inspector:deactivate`,
      `shopify:section:load`,
      `shopify:section:unload`,
      `shopify:section:select`,
      `shopify:section:deselect`,
      `shopify:section:reorder`,
      `shopify:block:select`,
      `shopify:block:deselect`,
    ];
    editorEvents.forEach((event) => {
      this.addEventListener(event, this.handleEditorEvent.bind(this));
    });
  }

  init() {
    this.els.controlScroll = this.querySelector(`.nss-tabs-controls__scroll-wrapper`);
    this.els.controls = this.querySelectorAll(`nss-tab-control`);
    this.els.groups = this.querySelectorAll(`nss-tab-group`);

    this.enableAutoswitch = this.getAttribute(`nss-enable-autoswitch`);
    this.autoswitchDelay = this.getAttribute(`nss-autoswitch-delay`);

    this.initLinkedVideoTarget();

    if (this.els.controls[0]) {
      const wrapperEl = this.els.controls[0].querySelector(`.nss-tab-control__wrapper`);
      this.applyExternalContent(wrapperEl);
      this.applyLinkedCopy(wrapperEl);
    }
  }

  // Optional: sync an nss-video per tab in an external container elsewhere on
  // the page (e.g. a sibling column outside this tabs-group's own DOM — see
  // "How to Enjoy"). Videos are matched to tabs by DOM order, same as
  // controls<->groups are matched by index elsewhere in this file.
  initLinkedVideoTarget() {
    const targetId = this.getAttribute(`nss-linked-video-target-id`);
    if (!targetId) return;

    const targetEl = document.querySelector(`#${targetId}`);
    if (!targetEl) return;

    targetEl.setAttribute(`data-nss-video-target`, `true`);
    this.els.linkedVideoTarget = targetEl;
    this.els.linkedVideos = targetEl.querySelectorAll(`nss-video`);

    if (this.els.linkedVideos[0]) this.applyLinkedVideo(0);
  }

  applyLinkedVideo(index) {
    if (!this.els.linkedVideos || !this.els.linkedVideos.length) return;

    this.els.linkedVideos.forEach((videoWrapEl, i) => {
      const videoEls = videoWrapEl.querySelectorAll(`video`);

      if (i === index) {
        videoWrapEl.classList.add(`nss-active`);
        // Browsers can skip painting a video's poster after flipping it from
        // display:none to visible when the poster finished loading while
        // still hidden (happens on the initially active tab, since it goes
        // visible on page load before the user ever interacts). Forcing a
        // layout read here triggers a repaint so the poster actually shows.
        void videoWrapEl.offsetHeight;
      } else {
        videoWrapEl.classList.remove(`nss-active`);
      }

      videoEls.forEach((videoEl) => {
        videoEl.pause();
        videoEl.currentTime = 0;
      });
      videoWrapEl.setAttribute(`nss-video-playing`, false);
    });
  }

  initAutoplay() {
    if (this.enableAutoswitch === "false") return;

    if (this.debug) console.log(`[NSS][Tabs Group] this.enableAutoswitch:`, this.enableAutoswitch);
    if (this.debug) console.log(`[NSS][Tabs Group] this.autoswitchDelay:`, this.autoswitchDelay);

    let current = 0;
    let max = this.els.controls.length;
    // let scrollOptions = {
    //   behavior: "smooth",
    //   block: "center",
    //   inline: "center",
    //   container: "nearest",
    // };

    setInterval(() => {
      if (this.autoswitchPause) return;

      current += 1;
      if (current == max) current = 0;

      /**
       * Safari doesn't support 'container' in scrollIntoView's options. This
       * means it effects every scroll container, causing the entire page to
       * scroll to this element at every call.
       */
      // this.els.controls[current].scrollIntoView(scrollOptions);

      this.scrollActiveControlIntoView(this.els.controls[current]);
      const id = this.els.controls[current].getAttribute(`nss-tab-control-id`);
      this.selectTabByControlId(id, { scrollControl: false });
    }, this.autoswitchDelay);
  }

  handleEditorEvent(e) {
    if (this.debug) console.log(`[NSS][Tabs Group] Handle editor event, e:`, e);

    switch (e.type) {
      case `shopify:block:select`:
        if (e.detail.blockId === this.getAttribute(`data-block-id`)) return;

        const markedEl = this.querySelector(`[data-block-id="${e.detail.blockId}"]`);
        if (!markedEl) return;

        const controlEl = markedEl.closest(`nss-tab-control`);
        if (controlEl && this.contains(controlEl)) {
          const id = controlEl.getAttribute(`nss-tab-control-id`);
          if (id != null) {
            this.selectTabByControlId(id, { scrollControl: true });
          }
          return;
        }

        const groupEl = markedEl.closest(`nss-tab-group`);
        if (groupEl && this.contains(groupEl)) {
          const id = groupEl.getAttribute(`nss-tab-group-id`);
          if (id != null) {
            this.selectTabByControlId(id, { scrollControl: true });
          }
        }
        break;
    }
  }

  selectTabByControlId(id, options = {}) {
    const controlEl = this.querySelector(`nss-tab-control[nss-tab-control-id="${id}"]`);
    const groupEl = this.querySelector(`nss-tab-group[nss-tab-group-id="${id}"]`);
    if (!controlEl || !groupEl) return;

    this.els.controls.forEach((c) => c.classList.remove(`nss-active`));
    this.els.groups.forEach((g) => g.classList.remove(`nss-active`));

    const cardEls = groupEl.querySelectorAll(`.nss-card-product__wrapper`);
    cardEls.forEach((cardEl) => cardEl.classList.remove(`nss-has-hovered`));

    controlEl.classList.add(`nss-active`);
    groupEl.classList.add(`nss-active`);

    const wrapperEl = controlEl.querySelector(`.nss-tab-control__wrapper`);
    if (wrapperEl) {
      this.applyExternalContent(wrapperEl);
      this.applyLinkedCopy(wrapperEl);
    }

    this.applyLinkedVideo(parseInt(id, 10));

    this.updateAllCarousels();
    this.stopAllVideos();

    // Resume the active panel's autoplay background videos — browsers can
    // pause them while the panel was hidden, and autoplay won't re-fire.
    groupEl.querySelectorAll(`video[autoplay]`).forEach((videoEl) => {
      if (videoEl.paused) videoEl.play().catch(() => {});
    });

    if (options.scrollControl) {
      this.scrollActiveControlIntoView(controlEl);
    }
  }

  scrollActiveControlIntoView(controlEl) {
    const scrollEl = this.els.controlScroll;
    if (!scrollEl || !controlEl) return;

    const scrollRect = scrollEl.getBoundingClientRect();
    const rect = controlEl.getBoundingClientRect();
    const x = controlEl.offsetLeft;
    const newLeft = x - scrollRect.width / 2 + rect.width / 2;

    scrollEl.scrollTo({
      behavior: `smooth`,
      top: 0,
      left: newLeft,
    });
  }

  handleClick(e) {
    const controlEl = e.target.closest(`nss-tab-control`);
    if (!controlEl || !this.contains(controlEl)) return;

    const id = controlEl.getAttribute(`nss-tab-control-id`);
    if (id == null) return;

    this.selectTabByControlId(id, { scrollControl: false });
  }

  handleMouseEnter(e) {
    this.autoswitchPause = true;
  }

  handleMouseLeave(e) {
    this.autoswitchPause = false;
  }

  applyExternalContent(wrapperEl) {
    const externalSettings = wrapperEl.getAttribute(`nss-external-settings`);
    if (externalSettings === "true") {
      const extId = wrapperEl.getAttribute(`nss-external-target-id`);
      const extColorBg = wrapperEl.getAttribute(`nss-external-color-bg`);
      const extColorText = wrapperEl.getAttribute(`nss-external-color-text`);
      const extImageBg = wrapperEl.getAttribute(`nss-external-image-bg`);

      const extEl = document.querySelector(`#${extId}`);
      if (extEl) {
        extEl.style.backgroundColor = extColorBg;
        extEl.style.color = extColorText;

        if (extImageBg) {
          extEl.style.backgroundImage = `url('${extImageBg}')`;
        }
      }
    }
  }

  // Swap a heading and/or paragraph elsewhere on the page to this tab's copy.
  // Each target is matched by the suffix of its block id (nss-text emits
  // data-block-id), so both the desktop and mobile variants update together.
  applyLinkedCopy(wrapperEl) {
    if (!wrapperEl) return;

    const swap = (idAttr, textAttr) => {
      const idSuffix = wrapperEl.getAttribute(idAttr);
      const text = wrapperEl.getAttribute(textAttr);
      if (!idSuffix || text == null) return;
      document.querySelectorAll(`[data-block-id$="${idSuffix}"]`).forEach((el) => {
        el.textContent = text;
      });
    };

    swap(`nss-link-heading-id`, `nss-link-heading`);
    swap(`nss-link-body-id`, `nss-link-body`);
  }

  updateAllCarousels() {
    const carouselEls = this.querySelectorAll(`nss-carousel`);

    carouselEls.forEach((carouselEl) => {
      carouselEl.swiper.update();
    });
  }

  stopAllVideos() {
    const videoEls = this.querySelectorAll(`video`);

    videoEls.forEach((videoEl) => {
      if (videoEl.getAttribute(`no-stop`) === "true") return;
      // Autoplay background videos (persona ovals) manage themselves — pausing
      // them here killed them permanently on every tab switch.
      if (videoEl.hasAttribute(`autoplay`)) return;
      videoEl.pause();
      videoEl.muted = true;
      videoEl.parentElement.setAttribute(`nss-video-playing`, false);
    });
  }
}

let elementName = `nss-tabs-group`;
if (typeof customElements.get(elementName) === `undefined`) {
  customElements.define(elementName, NSSTabsGroup);
} else {
  console.warn(`[NSS] Element '${elementName}' already defined.`);
}
